import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import LeftAngle from '../../assets/icons/left-angle.svg';
import RightAngle from '../../assets/icons/right-angle.svg';
import LoadingSpinner from '../loadingSpinner/loadingSpinner';
import InfoIcon from '../../assets/icons/info-icon.svg';
import EmptyBigCard from '../dashboard/dashboardCards/emptyBigCard';
import { TOP_PRODUCTS_FOR_AD } from '../../constants/pageIdentifiers';
import { useDashboardFilterDateStore } from '../../store/dateRangeStore';
import AdsReportTable from '../../partials/adsReportTable';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import VatDisplayer from '../../partials/VATDisplayer';
import { useGetTopProductsForAd } from '../../apiHooks/queries/reports/useGetTopProductsForAd';

interface TopProductsForAdReportProps {
  adCode?: string;
}

const fixedColumns: TableColumnInterface[] = [
  {
    field: 'itemName',
    headerName: 'productName',
    cellRenderer: (params: string) => (
      <Tooltip arrow placement="top" title={params}>
        <span className="max-w-[190px] truncate 2xl:max-w-[250px]">{params || '-'}</span>
      </Tooltip>
    ),
  },
];

const columns: TableColumnInterface[] = [
  {
    field: 'quantity',
    headerName: 'quantity',
    cellRenderer: (params) => (
      <span>{params ? Math.round(params).toLocaleString() : 0}</span>
    ),
  },
  {
    field: 'revenue',
    headerName: 'revenue',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
  {
    field: 'profit',
    headerName: 'profit',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
];

const TopProductsForAdReport: FC<TopProductsForAdReportProps> = ({ adCode }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const dashboardFilterDateRange = useDashboardFilterDateStore((state) => state.dashboardFilterDateRange);

  const { data, isFetching, isError } = useGetTopProductsForAd(
    TOP_PRODUCTS_FOR_AD,
    adCode || '',
    dashboardFilterDateRange[0],
    dashboardFilterDateRange[1],
    page,
  );

  const reportData = data?.data?.data?.report;
  const hasNextPage = data?.data?.data?.hasNextPage;

  return (
    <div className="flex h-full flex-col text-clip rounded-lg">
      <div className="flex max-h-[47px] items-center justify-between border-b border-dark px-3 py-2">
        <div className="flex items-center gap-4">
          <p className="text-lg font-medium leading-[36px] text-primary xl:text-base">
            {t('topProductsForAd')}
          </p>
          <Tooltip title={t('topProductsForAdInfo')}>
            <img src={InfoIcon} alt="" className="max-w-[20px]" />
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 0 || isFetching}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <img src={LeftAngle} alt="Previous" className="h-4 w-4" />
          </button>
          <button
            onClick={() => setPage(page + 1)}
            disabled={!hasNextPage || isFetching}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <img src={RightAngle} alt="Next" className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="flex h-[465px] w-full flex-col">
        {!isFetching && !isError && reportData === null && <EmptyBigCard />}
        {isFetching && <LoadingSpinner center color="primary" />}
        {isError && !isFetching && <p>{t('errorRetrievingData')}</p>}
        {!isFetching && !isError && reportData !== null && (
          <AdsReportTable
            data={reportData}
            isFetching={isFetching}
            isError={isError}
            columns={columns}
            fixedColumns={fixedColumns}
          />
        )}
      </div>
    </div>
  );
};

export default TopProductsForAdReport;
